<script setup>
import { ref, computed, provide } from "vue"
import { saveAs } from "file-saver"
import { useQuery } from "@vue/apollo-composable"
import { useLazyQuery } from "@vue/apollo-composable"
import { DateTime } from "luxon"
import { InformationCircleIcon } from "@heroicons/vue/24/outline"

import XButton from "@/components/common/XButton.vue"

import PaymentDetails from "@/pages/account/PaymentDetails.vue"

import ME_QUERY from "@/graphql/User/MeQuery.gql"
import GET_USER_PAYMENT_HISTORY from "@/graphql/PaymentOption/UserPaymentHistory.gql"
import WISE_GET_TRANSFER_RECEIPT from "@/graphql/PaymentOption/WiseGetTransferReceipt.gql"

const { result: user, loading, error, refetch } = useQuery(ME_QUERY)

const me = computed(() => user.value?.me || {})
const wiseTransferId = ref(null)
const receiptLoading = ref(false)
const paymentSummary = computed(() => user.value?.me.billingContact || {})

// Computed properties for formatted payment data
const formattedPaymentTerms = computed(() => {
  return paymentSummary.value.paymentTerms 
    ? paymentSummary.value.paymentTerms.replace('NET', 'Net ')
    : 'Not specified'
})

const formattedBalance = computed(() => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(paymentSummary.value.paymentBalance ?? 0)
})

const formattedConfirmationDate = computed(() => {
  if (!paymentSummary.value?.confirmedAsDate) return ''
  return DateTime.fromFormat(
    paymentSummary.value.confirmedAsDate,
    'yyyy-MM-dd HH:mm:ss',
  ).toFormat('LLLL d, yyyy')
})

const hasPaymentMethod = computed(() => {
  return !!(user.value?.me.userDetails.wiseAccountId || user.value?.me.userDetails.cryptoWallet?.walletId)
})

const nextPaymentDateText = computed(() => {
  if (!hasPaymentMethod.value) {
    return '⚠️ Complete your payment details'
  }
  
  if (!paymentSummary.value.paymentBalance) {
    return 'N/A'
  }
  
  if (paymentSummary.value.paymentMessage) {
    return paymentSummary.value.paymentMessage
  }
  
  if (paymentSummary.value.nextPaymentDate) {
    return DateTime.fromFormat(
      paymentSummary.value.nextPaymentDate,
      'yyyy-MM-dd HH:mm:ss',
    ).toFormat('LLLL d, yyyy')
  }
  
  return 'Not scheduled'
})

const nextPaymentDateClass = computed(() => {
  return hasPaymentMethod.value && nextPaymentDateText.value !== 'N/A' ? 'text-accent' : 'font-light text-base-content/60 text-xs'
})
const refetchUserData = async () => {
  await refetch()
}

const {
  load,
  refetch: refetchReceipt,
  result: wiseTransferResult,
} = useLazyQuery(
  WISE_GET_TRANSFER_RECEIPT,
  () => ({
    filters: {
      transferId: wiseTransferId.value,
    },
  }),
  () => ({
    fetchPolicy: "no-cache",
  }),
)

function openUrl(url) {
  window.open(url, "_blank")
}

const downloadTransferReceipt = async transferId => {
  receiptLoading.value = true
  try {
    wiseTransferId.value = transferId

    let queryResult = null
    // Fetch the receipt data
    queryResult = await load()

    if (!queryResult) {
      queryResult = await refetchReceipt()
    }
    //don't remove, issue sometimes hiting the download to quick???
    await new Promise(r => setTimeout(r, 2000))
    const pdfRaw = wiseTransferResult?.value?.wiseGetTransferReceipt?.pdfData

    if (!pdfRaw) {
      receiptLoading.value = false
      console.error("No PDF data found for transfer receipt.")
      return
    }

    // Convert Base64 string to binary
    const bytes = base64ToUint8Array(pdfRaw)

    // Create a Blob and trigger file download
    const blob = new Blob([bytes], { type: "application/pdf" })
    triggerFileDownload(blob, `wise_receipt_${transferId}.pdf`)
    wiseTransferId.value = transferId
    receiptLoading.value = false
  } catch (error) {
    wiseTransferId.value = transferId
    console.error("Error fetching transfer receipt:", error)
    receiptLoading.value = false
  }
}

// Helper function: Convert Base64 to Uint8Array
const base64ToUint8Array = base64 => {
  const binaryString = atob(base64)
  return new Uint8Array([...binaryString].map(char => char.charCodeAt(0)))
}

// Helper function: Trigger file download
const triggerFileDownload = (blob, filename) => {
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()

  // Clean up
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}

provide("refetchUserData", refetchUserData)

const {
  result: userPaymentHistory,
  loading: userPaymentHistoryLoading,
  error: userPaymentHistoryError,
} = useQuery(
  GET_USER_PAYMENT_HISTORY,
  () => ({
    filters: {
      userId: {
        value: [user.value?.me.id],
      },
      status: {
        value: ["cancelled"],
        modifiers: {
          matchType: "excludes",
        },
      },
    },
    first: 300,
    orderBy: [{ column: "created", order: "DESC" }],
  }),
  { enabled: computed(() => !!user.value?.me) },
)
</script>

<template>
  <div class="mx-auto pb-20 md:pb-0" v-if="user?.me">
    <div
      class="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-12 gap-y-8 xl:mx-0 xl:max-w-none xl:grid-cols-3"
    >
      <!-- side content User Payment Summary -->
      <div class="xl:col-start-3 xl:row-end-1">
        <SectionHeader>
              <h2>Next Payment Summary</h2>
            </SectionHeader>
        <div class="panel">
          <!-- Payment Summary Grid -->
          <div class="payment-widget">
            <dl class="payment-summary-grid">
              <div>
                <dt>Frequency</dt>
                <dd>{{ paymentSummary.paymentFrequency || 'Not specified' }}</dd>
              </div>

              <div>
                <dt>Term</dt>
                <dd>{{ formattedPaymentTerms }}</dd>
              </div>

              <div>
                <dt>Balance</dt>
                <dd>
                  <span class="text-accent">{{ formattedBalance }}</span>
                  <small v-if="paymentSummary?.confirmedAsDate" class="block text-xs text-neutral-content/80 -mt-1 font-light">
                    as of {{ formattedConfirmationDate }}
                  </small>
                </dd>
              </div>

              <div>
                <dt>Threshold</dt>
                <dd>$250.00</dd>
              </div>

              <div class="sm:col-span-2 pt-2">
                <dt>Next Payment Date</dt>
                <dd>
                  <span :class="nextPaymentDateClass">{{ nextPaymentDateText }}</span>
                </dd>
              </div>
            </dl>
          </div>

          <!-- Payment Notice -->
          <aside class="border-t border-neutral-content/10 bg-base-100/50 p-4 relative" role="note" aria-label="Payment processing notice">
            <div class="flex gap-2.5 items-center">
              <InformationCircleIcon class="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <p class="text-xs text-neutral-content/70 leading-tight">
                Please allow 2-3 business days for changes to payment information to be updated in your account.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <!-- main content -->
      <div class="xl:col-span-2 xl:row-span-2 xl:row-end-2">
        <div>
          <div>
            <SectionHeader>
              <h2>Payment Details</h2>
            </SectionHeader>
            <div>
              <PaymentDetails :user="me" />
            </div>
          </div>

          <div class="grid overflow mt-6">
            <SectionHeader>
              <h2>Payment History</h2>
            </SectionHeader>
            <div class="mb-8">
              <XTable
                :data="userPaymentHistory?.userPaymentHistory?.data || []"
                :isLoading="userPaymentHistoryLoading"
                emptyText="No Payment History Found"
              >
                <template v-slot:header-row>
                  <th>Date</th>
                  <th>Amount (USD)</th>
                  <th>Payment Type</th>
                  <th>Status</th>
                  <th>Receipt</th>
                </template>
                <template v-slot:row="props">
                  <td>
                    {{
                      DateTime.fromFormat(
                        props.row.created,
                        "yyyy-MM-dd",
                      ).toFormat("LLLL d, yyyy")
                    }}
                  </td>
                  <td style="width: 120px">
                    {{
                      Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(props.row.sourceValue)
                    }}
                  </td>
                  <td v-if="props.row.paymentType == 'bitcoin'">
                    Crypto Wallet (BTC)
                  </td>
                  <td v-else>Bank Transfer (via Wise)</td>
                  <td>
                    {{
                      props.row.status
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, char => char.toUpperCase())
                    }}
                  </td>
                  <td v-if="props.row.paymentType == 'bitcoin'">
                    <XButton
                      class="btn btn-ghost hover:bg-neutral/30 text-base-content/40 hover:text-base-content/70"
                      :is-loading="receiptLoading"
                      @click="openUrl(props.row.transactionUrl)"
                      >Open Transaction URL
                    </XButton>
                  </td>
                  <td v-else>
                    <XButton
                      class="btn btn-ghost hover:bg-neutral/30 text-base-content/40 hover:text-base-content/70"
                      :is-loading="receiptLoading"
                      @click="downloadTransferReceipt(props.row.transferId)"
                      >Download Receipt
                    </XButton>
                  </td>
                </template>
              </XTable>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
dl {
  @apply grid grid-cols-1 gap-y-4;
}

.payment-summary-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 gap-4;
}

.payment-summary-grid > div {
  @apply min-h-[3rem] flex flex-col;
}


/* Make payment summary values more prominent */
.payment-summary-grid dt {
  @apply text-xs text-neutral-content/60  tracking-wide uppercase;
}

.payment-summary-grid dd {
  @apply text-sm text-base-content mt-2 font-medium;
}
.payment-widget-wrapper {
  @apply rounded-md border bg-base-200/40 dark:bg-neutral border-neutral-content/20;
}
.payment-widget-wrapper-divider {
  @apply divide-y divide-neutral-content/20;
}
.payment-widget {
  @apply p-5 grid grid-cols-1 gap-y-6;
}
.collapse {
  border-radius: 0;
}
.collapse-content {
  @apply peer-checked:bg-base-100/40 peer-checked:dark:bg-neutral-content/10 peer-checked:border-neutral-content/20 peer-checked:border-b peer-checked:border-l peer-checked:border-r rounded-b-lg;
}
.collapse-title {
  @apply peer-checked:bg-base-100/40  peer-checked:dark:bg-neutral-content/10 peer-checked:border-neutral-content/20 peer-checked:border-t peer-checked:border-l peer-checked:border-r rounded-t-lg;
  padding: 0.6rem;
  min-height: initial;
}
.collapse-title,
:where(.collapse > input[type="checkbox"]),
:where(.collapse > input[type="radio"]) {
  min-height: initial;
}
.collapse-arrow > .collapse-title:after {
  @apply text-neutral-content/30;
  top: 1.3rem;
}
.divider {
  @appy mt-2;
}

.account-details-container {
  @apply bg-base-100 w-full text-sm text-neutral-content rounded-md px-2.5 shadow-sm ring-1 ring-inset ring-neutral relative min-h-[2.5rem] flex items-center;
}
.account-details-label {
  @apply absolute text-sm text-accent scale-75 -top-[0.5rem] -left-[0.2rem];
}
</style>
